import {useSelector} from 'react-redux';

import awsRumActionTypes from '../consts/aws/awsRumActionTypes';
import awsRumStatusTypes from '../consts/aws/awsRumStatusTypes';
import {LOADER_TYPES} from '../consts/loader/loaderTypes';
import QUERY_PARAMS from '../consts/route/queryParams';
import QUERY_PARAM_VALUES from '../consts/route/queryParamValues';
import routes from '../consts/routes';
import messageCenterService from '../containers/MessageCenter/messageCenterService';
import {useNavigationState} from '../contexts/NavigationContext';
import damService from '../services/dam/damService';
import ecPushObserver from '../services/events/ecPushObserver';
import rumCustomEvents from '../services/events/rumCustomEvents';
import loaderService from '../services/loader/loaderService';
import log from '../services/logger/log';
import {getRouteByDestinationType} from '../services/notificationDestinationMapping';
import notificationService from '../services/notificationService';
import {selectIsAuthorized} from '../state/selectors/auth';
import {
    selectCurrentDusn,
    selectDeviceLastChargerDusn,
    selectDusnByAssetId,
    selectDusnByCodentify,
    selectIsDeviceRegisteredByDusn,
    selectIsHolderType,
} from '../state/selectors/iotDevices';
import {setCurrentDusn} from '../state/slice/iotDevices';
import {dispatch, getState} from '../state/store';
import useNavigateReplace from './useNavigateReplace';

const PUSH_NOTIFICATION_PAGE_ID = 'Push notification';

const getIsPushNotificationStart = () =>
    new URLSearchParams(window.location.search).get(QUERY_PARAMS.EVENT_TYPE) ===
    QUERY_PARAM_VALUES.EVENT_TYPE_PUSH_NOTIFICATION_CLICK;

const validateDusn = (dusn) => {
    const isDeviceRegistered = selectIsDeviceRegisteredByDusn(dusn)(getState());

    if (!isDeviceRegistered) {
        notificationService.showDeviceIsNotRegisteredWarning();

        throw new Error('Device is not registered');
    }

    return dusn;
};

const getDusn = (initialDusn, codentify, assetId) => {
    if (initialDusn) {
        return validateDusn(initialDusn);
    }

    if (!codentify && !assetId) return null;

    const dusn = selectDusnByCodentify(codentify)(getState()) || selectDusnByAssetId(assetId)(getState());

    return validateDusn(dusn);
};

const getChargerDusn = (dusn) => {
    const isHolder = selectIsHolderType(dusn)(getState());
    let chargerDusn = dusn;

    if (isHolder) {
        chargerDusn = selectDeviceLastChargerDusn(dusn)(getState());
    }

    return validateDusn(chargerDusn);
};

const handleNotificationDusn = (dusn, codentify, assetId) => {
    if (!dusn && !codentify && !assetId) return null;

    const resolvedDusn = getDusn(dusn, codentify, assetId);

    if (!resolvedDusn) return null;

    const chargerDusn = getChargerDusn(resolvedDusn);

    if (chargerDusn) {
        dispatch(setCurrentDusn(chargerDusn));
    }

    return resolvedDusn;
};

const useOnPushNotificationClick = () => {
    const isAuthorized = useSelector(selectIsAuthorized);
    const navigate = useNavigateReplace();
    const {setBackRoute} = useNavigationState({defaultRoute: routes.HOME});

    const handleDestination = (data) => {
        const {destinationType, notificationSubType, isNativeNotification, backRoute} = data || {};

        if (!destinationType) return;

        const {route, isBackRouteAvailable, isDeviceRequired} = getRouteByDestinationType({
            destinationType,
            notificationSubType,
            isNativeNotification,
        });

        if (isDeviceRequired) {
            const currentDusn = selectCurrentDusn(getState());

            if (!currentDusn) {
                throw new Error('No devices available');
            }
        }

        if (route) {
            navigate(route);
        }

        if (backRoute && isBackRouteAvailable) {
            setBackRoute(backRoute);
        } else {
            // Clear back route that was set before redirecting to message center
            setBackRoute(null);
        }
    };

    const onClick = async (data) => {
        // dusn, isMock - used only for mock in-app push notifications
        const {
            destinationType,
            notificationType,
            notificationSubType,
            titleText,
            messageText,
            campaignName,
            codentify, // used for push notifications and messages in the message center
            assetId,
            timestamp,
            id,
            backRoute,
            isMock,
            isRead, // always false for push notifications
            isNativeNotification,
        } = data || {};

        let dusn = data?.dusn;

        const rumOptions = {
            eventId: id,
            ...(getIsPushNotificationStart() && {pageId: PUSH_NOTIFICATION_PAGE_ID}),
            startTime: timestamp,
            destinationType,
            notificationType,
            notificationSubType,
            titleText,
            messageText,
            action: awsRumActionTypes.CLICK,
        };

        log.debug(`useOnPushNotificationClick: Start handling notification click, id: ${id}`);

        ecPushObserver.observe({messageId: id, notificationSubType});

        try {
            // Prevent the case: click a native push -> open the app -> the session is expired
            if (!isAuthorized) {
                throw new Error('Session is expired');
            }

            dusn = handleNotificationDusn(dusn, codentify, assetId);

            handleDestination({destinationType, notificationSubType, isNativeNotification, backRoute});

            log.debug(`useOnPushNotificationClick: Successfully handled notification click, id: ${id}`);
            rumCustomEvents.pushPushNotification({...rumOptions, dusn, status: awsRumStatusTypes.SUCCESS});
        } catch (e) {
            // Clear back route that was set before redirecting to message center
            setBackRoute(null);
            navigate(routes.HOME);

            rumCustomEvents.pushPushNotification({...rumOptions, dusn, status: awsRumStatusTypes.FAILED});

            log.error(`useOnPushNotificationClick: Failed handling notification click, id: ${id}`, e?.message);
        }

        if (!isRead && !isMock) {
            messageCenterService.readMessage(id);
            damService.trackNotification(dusn, campaignName, notificationType);
        }

        loaderService.hideGlobalLoaderByType({type: LOADER_TYPES.NOTIFICATION_LOADER});
    };

    return onClick;
};

export default useOnPushNotificationClick;
